/**
 * NayaxSalesAudit — Phase 87bf (Feb 2026)
 *
 * Audit des ventes Nayax vs AgroManage pour UNE journée donnée, tous distributeurs.
 *
 * Cas d'usage : l'opérateur voit "67 ventes sur Moma" mais "58 dans AgroManage"
 * pour la veille. Cette page lui montre :
 *   - Le delta total (67 - 58 = 9)
 *   - La répartition par distributeur
 *   - La liste des 9 manquantes avec leur raison (unmapped / insufficient_stock / before_cutoff / etc.)
 *   - Un indicateur si la fenêtre Nayax couvre bien le jour demandé (oldest_seen)
 */
import React, { useState } from "react";
import { api } from "../../lib/api";
import { fmtDateTime, fmtNum } from "../../lib/format";
import {
  AlertCircle,
  CalendarSearch,
  Check,
  Clock,
  Loader2,
  RefreshCw,
  Search,
  Undo2,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";

const STATUS_META = {
  ready: { label: "Prête (pas encore synchro)", color: "bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]" },
  unmapped: { label: "Non mappée", color: "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]" },
  insufficient_stock: { label: "Stock insuffisant", color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  before_cutoff: { label: "Avant cutoff", color: "bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]" },
  empty_name: { label: "Refund / Void", color: "bg-[#F4F4F0] text-[#A3A6A3] border-[#C8C8C0]" },
  ignored: { label: "Enterrée", color: "bg-[#EFEFEA] text-[#7A7A75] border-[#D0D0CA]" },
};

function yesterdayISO() {
  // Phase 98b — Hier en heure suisse (pas UTC) pour matcher la vision Moma.
  const d = new Date();
  const swissDate = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Zurich" }));
  swissDate.setDate(swissDate.getDate() - 1);
  const y = swissDate.getFullYear();
  const m = String(swissDate.getMonth() + 1).padStart(2, "0");
  const day = String(swissDate.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * RecoverButton — Phase 101ex (Feb 2026)
 *
 * Bouton "Récupérer" sur une tx Nayax classée "Unknown(SLOT = PRIX)" — càd
 * une vraie vente survenue AVANT que le catalogue Nayax soit mappé au produit.
 * L'audit backend a détecté que ce slot porte aujourd'hui le produit
 * `tx.suggested_product` — on propose une reprise manuelle assumée par l'admin :
 * si c'était bien ce produit-là à l'époque, on crée rétroactivement la Sale
 * (marquée needs_review + review_reason="recovered_from_unknown").
 */
function RecoverButton({ tx, onRecovered }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState(
    tx.suggested_price != null ? String(tx.suggested_price) : ""
  );
  const [decrementStock, setDecrementStock] = useState(true);

  const suggested = tx.suggested_product || {};

  async function confirm() {
    const parsed = Number(String(price).replace(",", "."));
    if (!parsed || parsed <= 0) {
      toast.error("Prix unitaire invalide");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/nayax/recover-unknown-tx", {
        distributor_id: tx.distributor_id,
        external_id: String(tx.transaction_id),
        product_id: suggested.id,
        unit_price: parsed,
        quantity: Number(tx.quantity) || 1,
        sold_at: tx.sold_at,
        decrement_stock: decrementStock,
      });
      toast.success(
        `Vente récupérée : ${data.product_name} — ${Number(data.amount).toFixed(2)} CHF` +
          (data.lot_decremented ? " (stock décrémenté)" : "")
      );
      setOpen(false);
      onRecovered && onRecovered();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Échec de la récupération";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF3EE]"
        onClick={() => setOpen(true)}
        data-testid={`audit-recover-${tx.transaction_id}`}
      >
        <Undo2 className="w-3 h-3 mr-1" />
        Récupérer
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="audit-recover-dialog">
          <DialogHeader>
            <DialogTitle>Récupérer cette vente « Unknown »</DialogTitle>
            <DialogDescription className="text-xs text-[#575A57] pt-2">
              Cette tx Nayax est arrivée sans nom produit (« {tx.raw_product_name} »).
              Le slot <strong>{tx.suggested_slot}</strong> porte aujourd&apos;hui le
              produit ci-dessous. Si c&apos;était déjà ce produit à l&apos;époque, tu
              peux créer rétroactivement la vente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="p-3 bg-[#F4F4F0] rounded border border-[#E5E5E0]">
              <div className="text-[10px] uppercase tracking-wide text-[#A3A6A3]">
                Produit proposé (slot {tx.suggested_slot})
              </div>
              <div className="text-sm font-medium text-[#1A1C1A]">
                {suggested.name}
                {suggested.sku && (
                  <span className="text-xs font-mono text-[#A3A6A3] ml-2">
                    {suggested.sku}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-[#575A57] mt-1">
                {tx.distributor_name} · {fmtDateTime(tx.sold_at)} · qté {fmtNum(tx.quantity)}
              </div>
            </div>

            <div>
              <label className="text-xs text-[#575A57] block mb-1">
                Prix unitaire (CHF)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="audit-recover-price"
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-[#1A1C1A] cursor-pointer">
              <input
                type="checkbox"
                checked={decrementStock}
                onChange={(e) => setDecrementStock(e.target.checked)}
                data-testid="audit-recover-decrement"
              />
              Décrémenter le stock FIFO (recommandé si le stock n&apos;a jamais été
              corrigé pour cette vente)
            </label>

            <div className="text-[10px] text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] rounded p-2">
              La vente sera marquée <strong>à revalider</strong>
              (motif : <em>recovered_from_unknown</em>) pour audit HACCP.
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="audit-recover-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={confirm}
              disabled={busy}
              className="bg-[#1F4E3D] hover:bg-[#173C2F] text-white"
              data-testid="audit-recover-confirm"
            >
              {busy ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Traitement…
                </>
              ) : (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Confirmer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function NayaxSalesAudit() {
  const [date, setDate] = useState(yesterdayISO());
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const runAudit = async () => {
    if (!date) {
      toast.error("Sélectionne une date");
      return;
    }
    setLoading(true);
    setAudit(null);
    try {
      // Phase 98b — Ajout tz=Europe/Zurich pour aligner sur la journée civile suisse
      // (fixe l'écart de ~2 ventes/jour qui étaient dans la nuit 00h-02h heure locale)
      // Phase 98c — Timeout étendu à 90s (l'audit peut prendre plusieurs secondes
      // en prod : parallélisé à 20s max côté backend, mais on laisse une marge.)
      const { data } = await api.get(
        `/nayax/sales-audit?date=${encodeURIComponent(date)}&tz=Europe/Zurich`,
        { timeout: 90000 },
      );
      setAudit(data);
      if (data.delta === 0 && data.total_nayax_on_date > 0) {
        toast.success(`Aucun écart : ${data.total_nayax_on_date} ventes Nayax = ${data.total_am_on_date} ventes AM`);
      } else if (data.delta > 0) {
        toast.warning(`${data.delta} vente(s) manquante(s) en AgroManage`);
      } else if (data.delta < 0) {
        toast.info(`AM a ${-data.delta} vente(s) en plus que Nayax (possible double sync ou tx Nayax hors fenêtre)`);
      } else {
        toast.info("Aucune vente Nayax remontée pour cette date");
      }
    } catch (e) {
      // Phase 98c — Message d'erreur riche : détaille statut HTTP + code + timeout,
      // au lieu du fallback générique "Erreur audit" qui masquait la cause.
      let msg;
      if (e?.code === "ECONNABORTED" || (e?.message || "").toLowerCase().includes("timeout")) {
        msg = "L'audit a dépassé 90s (Nayax lent ou trop de distributeurs). Réessaie.";
      } else if (e?.response?.status) {
        const status = e.response.status;
        const detail = e.response?.data?.detail || e.response?.statusText || "";
        msg = `Erreur audit (HTTP ${status}) : ${detail || "réponse serveur invalide"}`;
      } else if (e?.message) {
        msg = `Erreur audit : ${e.message}`;
      } else {
        msg = "Erreur audit (inconnue)";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const missing = audit?.missing || [];
  const filteredMissing = missing.filter((m) => {
    if (filter !== "all" && m.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        (m.raw_product_name || "").toLowerCase().includes(q) ||
        (m.transaction_id || "").toLowerCase().includes(q) ||
        (m.distributor_name || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="nayax-sales-audit">
      {/* Header + date picker */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#575A57] block mb-1">
                Date à auditer (heure suisse)
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-[200px]"
                data-testid="audit-date-input"
              />
            </div>
            <Button
              onClick={runAudit}
              disabled={loading || !date}
              className="bg-[#1F4E3D] hover:bg-[#163826] text-white"
              data-testid="audit-run-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarSearch className="w-4 h-4 mr-2" />
              )}
              Comparer Nayax vs AgroManage
            </Button>
            {audit?.cutoff_date && (
              <Badge variant="outline" className="self-center">
                <Clock className="w-3 h-3 mr-1" /> Cutoff : {audit.cutoff_date}
              </Badge>
            )}
          </div>
          <p className="text-xs text-[#A3A6A3] mt-3 leading-relaxed">
            Récupère les ventes Nayax du jour sélectionné (tous les distributeurs liés) et compare avec
            les ventes enregistrées dans AgroManage. Les ventes manquantes sont classifiées par raison
            (non mappée, stock insuffisant, avant cutoff, refund, enterrée, prête à sync…).
          </p>
        </CardContent>
      </Card>

      {/* Erreurs de fetch éventuelles */}
      {audit?.fetch_errors?.length > 0 && (
        <div className="bg-[#FBEEEA] border border-[#E8B0A0] rounded p-4 text-sm" data-testid="audit-fetch-errors">
          <div className="flex items-center gap-2 mb-2 font-medium text-[#A04E2C]">
            <AlertCircle className="w-4 h-4" /> Erreurs API Nayax
          </div>
          <ul className="list-disc pl-5 text-[#A04E2C] space-y-0.5">
            {audit.fetch_errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Phase 87bg — Bandeau collision machine_id (CRITIQUE) */}
      {audit?.machine_collisions?.length > 0 && (
        <div className="bg-[#FBEEEA] border-2 border-[#A04E2C] rounded p-4" data-testid="audit-collision-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-[#A04E2C] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-[#A04E2C] mb-1">
                Collision détectée : plusieurs distributeurs partagent le même machine Nayax
              </div>
              <p className="text-sm text-[#7A3A20] mb-3">
                Quand 2+ distributeurs pointent sur le même <code>nayax_machine_id</code>, Nayax renvoie
                les MÊMES ventes pour chacun. La première sync gagne (idempotence par <code>TransactionID</code>),
                les autres reçoivent <strong>0 vente</strong> → l&apos;écart affiché ci-dessous est artificiellement gonflé.
                Va dans <strong>Distributeurs</strong> et corrige le <code>nayax_machine_id</code> de l&apos;un
                des distributeurs en doublon (vide ou ID Nayax distinct).
              </p>
              <ul className="text-xs space-y-1.5">
                {audit.machine_collisions.map((c, i) => (
                  <li key={i} className="font-mono">
                    <strong>Machine #{c.machine_id}</strong> →{" "}
                    {c.distributors.map((d) => d.distributor_name).join(" + ")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Synthèse globale */}
      {audit && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-summary">
          <div className="border rounded p-4 bg-white">
            <div className="text-xs uppercase tracking-wider text-[#A3A6A3]">Nayax / Moma</div>
            <div className="text-3xl font-bold text-[#1A1C1A] mt-1" data-testid="audit-total-nayax">
              {audit.total_nayax_on_date}
            </div>
            <div className="text-xs text-[#575A57] mt-1">
              {audit.machine_collisions?.length > 0 ? (
                <>
                  brut, dont <strong>{audit.effective_nayax_on_date}</strong> uniques (hors collisions)
                </>
              ) : (
                "ventes ce jour-là"
              )}
            </div>
          </div>
          <div className="border rounded p-4 bg-white">
            <div className="text-xs uppercase tracking-wider text-[#A3A6A3]">AgroManage</div>
            <div className="text-3xl font-bold text-[#1A1C1A] mt-1" data-testid="audit-total-am">
              {audit.total_am_on_date}
            </div>
            <div className="text-xs text-[#575A57] mt-1">ventes synchronisées en base</div>
          </div>
          <div
            className={`border rounded p-4 ${(audit.machine_collisions?.length > 0 ? audit.effective_delta : audit.delta) > 0 ? "bg-[#FBEEEA] border-[#E8B0A0]" : (audit.machine_collisions?.length > 0 ? audit.effective_delta : audit.delta) < 0 ? "bg-[#FBF4E9] border-[#E8D3A8]" : "bg-[#E8F2EC] border-[#A8C9B6]"}`}
          >
            <div className="text-xs uppercase tracking-wider opacity-75">
              Écart {audit.machine_collisions?.length > 0 && "(corrigé)"}
            </div>
            <div
              className={`text-3xl font-bold mt-1 ${(audit.machine_collisions?.length > 0 ? audit.effective_delta : audit.delta) > 0 ? "text-[#A04E2C]" : (audit.machine_collisions?.length > 0 ? audit.effective_delta : audit.delta) < 0 ? "text-[#8C6B2F]" : "text-[#1F4E3D]"}`}
              data-testid="audit-delta"
            >
              {(() => {
                const v = audit.machine_collisions?.length > 0 ? audit.effective_delta : audit.delta;
                return v > 0 ? `+${v}` : v;
              })()}
            </div>
            <div className="text-xs mt-1 opacity-75">
              {audit.machine_collisions?.length > 0 ? (
                <>écart réel hors doublons (brut : {audit.delta > 0 ? `+${audit.delta}` : audit.delta})</>
              ) : audit.delta > 0 ? (
                "ventes manquantes en AM"
              ) : audit.delta < 0 ? (
                "ventes en plus en AM"
              ) : (
                "écart parfait"
              )}
            </div>
          </div>
          <div className="border rounded p-4 bg-white">
            <div className="text-xs uppercase tracking-wider text-[#A3A6A3]">À investiguer</div>
            <div className="text-3xl font-bold text-[#5847A6] mt-1" data-testid="audit-missing-count">
              {audit.missing_count}
            </div>
            <div className="text-xs text-[#575A57] mt-1">tx Nayax non synchronisées ce jour</div>
          </div>
        </div>
      )}

      {/* Statut breakdown */}
      {audit?.status_breakdown && Object.keys(audit.status_breakdown).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="audit-status-breakdown">
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const count = audit.status_breakdown[key] || 0;
            if (count === 0 && filter !== key) return null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(filter === key ? "all" : key)}
                className={`border rounded p-3 text-left transition ${meta.color} ${filter === key ? "ring-2 ring-offset-1 ring-[#1F4E3D]" : "hover:opacity-90"}`}
                data-testid={`audit-filter-${key}`}
              >
                <div className="text-xs uppercase tracking-wider opacity-75">{meta.label}</div>
                <div className="text-2xl font-bold mt-1">{count}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Tableau par distributeur */}
      {audit && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-[#1A1C1A] mb-3">
              Détail par distributeur ({audit.per_distributor?.length || 0})
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distributeur</TableHead>
                  <TableHead>Machine #</TableHead>
                  <TableHead className="text-right">Nayax</TableHead>
                  <TableHead className="text-right">AgroManage</TableHead>
                  <TableHead className="text-right">Écart</TableHead>
                  <TableHead className="text-right">Manquantes</TableHead>
                  <TableHead>Fenêtre Nayax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.per_distributor?.map((d) => (
                  <TableRow key={d.distributor_id} data-testid={`audit-row-${d.distributor_id}`}>
                    <TableCell className="font-medium">{d.distributor_name}</TableCell>
                    <TableCell className="text-xs font-mono text-[#575A57]">#{d.machine_id}</TableCell>
                    <TableCell className="text-right font-mono">{d.nayax_count_on_date}</TableCell>
                    <TableCell className="text-right font-mono">{d.am_count_on_date}</TableCell>
                    <TableCell className="text-right font-mono">
                      {d.delta === 0 ? (
                        <Check className="w-4 h-4 text-[#1F4E3D] inline" />
                      ) : (
                        <span className={d.delta > 0 ? "text-[#A04E2C]" : "text-[#8C6B2F]"}>
                          {d.delta > 0 ? `+${d.delta}` : d.delta}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{d.missing_count}</TableCell>
                    <TableCell className="text-xs">
                      {d.error ? (
                        <span className="text-[#A04E2C]">{d.error}</span>
                      ) : d.window_covers_date ? (
                        <span className="text-[#1F4E3D]">
                          ✓ couvre la date ({d.nayax_total_returned} tx au total)
                        </span>
                      ) : (
                        <span className="text-[#8C6B2F]">
                          ⚠ {d.nayax_total_returned} tx — la plus ancienne :{" "}
                          {fmtDateTime(d.nayax_oldest_seen)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recherche + filtre */}
      {audit && missing.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A6A3]" />
            <input
              type="text"
              placeholder="Filtrer par nom Nayax, ID tx, distributeur…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 w-full border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4E3D]"
              data-testid="audit-search"
            />
          </div>
          {filter !== "all" && (
            <Button variant="ghost" size="sm" onClick={() => setFilter("all")} data-testid="audit-clear-filter">
              <X className="w-3 h-3 mr-1" /> Filtre : {STATUS_META[filter]?.label}
            </Button>
          )}
        </div>
      )}

      {/* Tableau des manquantes */}
      {audit && missing.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-[#1A1C1A] mb-3">
              Détail des ventes manquantes ({filteredMissing.length} / {missing.length})
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date/heure</TableHead>
                  <TableHead>Distributeur</TableHead>
                  <TableHead>Nom Nayax</TableHead>
                  <TableHead className="text-right">Qté</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Détail</TableHead>
                  <TableHead className="w-32">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMissing.map((tx) => (
                  <TableRow
                    key={tx.transaction_id}
                    data-testid={`audit-missing-${tx.transaction_id}`}
                  >
                    <TableCell className="text-xs font-mono whitespace-nowrap">
                      {fmtDateTime(tx.sold_at)}
                    </TableCell>
                    <TableCell className="text-xs">{tx.distributor_name}</TableCell>
                    <TableCell>
                      <div className="text-sm">{tx.raw_product_name || <em className="text-[#A3A6A3]">(vide)</em>}</div>
                      {tx.matched_product && (
                        <div className="text-[10px] text-[#1F4E3D] mt-0.5">
                          → {tx.matched_product.name}
                        </div>
                      )}
                      <div className="text-[10px] text-[#A3A6A3] font-mono mt-0.5">
                        #{tx.transaction_id}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNum(tx.quantity)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {tx.amount != null ? `${Number(tx.amount).toFixed(2)} CHF` : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_META[tx.status]?.color || ""} variant="outline">
                        {STATUS_META[tx.status]?.label || tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57]">{tx.details}</TableCell>
                    <TableCell>
                      {tx.recoverable && tx.suggested_product ? (
                        <RecoverButton tx={tx} onRecovered={runAudit} />
                      ) : (
                        <span className="text-[10px] text-[#A3A6A3]">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* État initial */}
      {!audit && !loading && (
        <div className="text-center text-sm text-[#A3A6A3] py-8" data-testid="audit-initial">
          Choisis une date puis clique sur « Comparer » pour lancer l&apos;audit.
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[#5847A6]" data-testid="audit-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Récupération depuis Nayax (toutes les machines)…</span>
        </div>
      )}

      {/* Bonus pédagogique */}
      {audit && audit.delta !== 0 && (
        <Card>
          <CardContent className="pt-6 text-xs text-[#575A57] space-y-2">
            <div className="font-medium text-sm text-[#1A1C1A]">Que faire avec l&apos;écart ?</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Non mappée</strong> : aller dans <em>Diagnostic Nayax</em> → cliquer sur le distributeur
                concerné → ajouter l&apos;alias depuis la suggestion 1 clic.
              </li>
              <li>
                <strong>Stock insuffisant</strong> : faire un approvisionnement du produit concerné sur ce
                distributeur, puis relancer une sync. (Astuce : passe le produit en stock simple « boisson »
                si tu n&apos;as pas envie de gérer les lots.)
              </li>
              <li>
                <strong>Prête (pas encore synchro)</strong> : prochain passage du scheduler (toutes les 15 min)
                ou clique sur « Sync Nayax » dans /ventes pour forcer.
              </li>
              <li>
                <strong>Avant cutoff</strong> : la date de coupure dans /parametres a été configurée APRÈS ces
                ventes. Désactive-la temporairement, relance la sync, puis remets-la.
              </li>
              <li>
                <strong>Refund / Void</strong> : ce sont des transactions Nayax sans produit (annulations).
                Comportement attendu, pas une vraie vente.
              </li>
              <li>
                <strong>Enterrée</strong> : tu as volontairement skip cette tx. Va dans <em>Diagnostic Nayax</em>
                pour la réactiver si besoin.
              </li>
              <li>
                <strong>Fenêtre Nayax ne couvre pas la date</strong> : le tableau par distributeur indique « ⚠ »
                quand la tx la plus ancienne renvoyée par Nayax est postérieure au jour audité — dans ce cas,
                Nayax a déjà sorti ces ventes de sa fenêtre `lastSales` et l&apos;audit n&apos;est plus fiable.
                Va sur la machine ou réimporte un CSV Moma de cette journée.
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
